import React, { useEffect, useState } from "react";
import { HexagonContainer, StyledSvg } from "../hexagon.styled";
import { useSearchParams } from "react-router-dom";
import { getFontColor } from "../../../utils/graphUtils";
import { useFilters } from "../../../../../hooks/useFilters";
import partnerIcon from "../../../../../Assets/Icons/PartnerLocation.svg";

const HexagonCells = ({
  size,
  color,
  isSecondColumn,
  isFirstColumn,
  isSecondRows,
  index,
  row,
  column,
  data,
  marketName,
  legendClickedValue,
  handleMouseEnter,
  handleMouseClick,
  handleMouseLeave,
  handleMouseMove,
  isClicked,
  filteredData,
  labelFontSize,
  labelFontWeight,
  labelFontColor,
  setIsClicked,
  loading,
  isAnyCellClicked,
  setIsAnyCellClicked,
  border,
}) => {
  // const getHexagonPath = (size) => {
  //   const points = [];
  //   for (let i = 0; i < 6; i++) {
  //     const angleDeg = 60 * i;
  //     const angleRad = (Math.PI / 180) * angleDeg;
  //     const x = size + size * Math.cos(angleRad);
  //     const y = size + size * Math.sin(angleRad);
  //     points.push(`${x},${y}`);
  //   }
  //   return `M ${points.join(' L ')} Z`;
  // };

  const getHexagonPath = (size, cornerRadius = 5) => {
    const points = [];
    for (let i = 0; i < 6; i++) {
      const angleDeg = 60 * i;
      const angleRad = (Math.PI / 180) * angleDeg;
      const x = size + size * Math.cos(angleRad);
      const y = size + size * Math.sin(angleRad);
      points.push({ x, y });
    }

    // Function to create rounded corners
    const roundedCorner = (p1, p2, p3, radius) => {
      const dx1 = p1.x - p2.x;
      const dy1 = p1.y - p2.y;
      const dx2 = p3.x - p2.x;
      const dy2 = p3.y - p2.y;

      const angle = (Math.atan2(dy1, dx1) - Math.atan2(dy2, dx2)) / 2;
      const tan = Math.abs(Math.tan(angle));
      let segment = radius / tan;

      const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
      const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
      const minLen = Math.min(len1, len2);

      if (segment > minLen) {
        segment = minLen; // Reassigning `segment`
        radius = segment * tan; // Reassigning `radius`
      }

      const x1 = p2.x + (dx1 / len1) * segment;
      const y1 = p2.y + (dy1 / len1) * segment;
      const x2 = p2.x + (dx2 / len2) * segment;
      const y2 = p2.y + (dy2 / len2) * segment;

      return `L ${x1} ${y1} Q ${p2.x} ${p2.y}, ${x2} ${y2}`;
    };

    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length; i++) {
      const p1 = points[(i - 1 + points.length) % points.length];
      const p2 = points[i];
      const p3 = points[(i + 1) % points.length];
      path += roundedCorner(p1, p2, p3, cornerRadius);
    }
    path += " Z";
    return path;
  };

  const [isLegendSelected, setIsLegendSelected] = useState(false);
  const [isSvgSelected, setIsSvgSelected] = useState(false);
  const [isDefaultState, setIsDefaultState] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const filteredKey = filteredData && Object.keys(filteredData)[0];
  const filteredValues = filteredData ? filteredData[filteredKey] : [];
  const selectedStateAbbrev = searchParams.get("state");

  const isRegionSelected = filteredKey === "region";
  const isCohortSelected = filteredKey === "cohort";

  const isTileClicked =
    isClicked?.row !== null &&
    isClicked?.column !== null &&
    isClicked?.data?.market === data?.market;

  // New state to track the hovered cell
  const [isHovered, setIsHovered] = useState(false);

  const { partnerExclusiveFlag } = useFilters();
  const showPartnerBadge = !!data?.isPartner && partnerExclusiveFlag;

  // The "partner" legend filter highlights all partner (ESA) regions instead of
  // matching on a performance bucket.
  const isPartnerFilter = legendClickedValue === "partner";
  const matchesLegend = isPartnerFilter
    ? !!data?.isPartner
    : data?.performance === legendClickedValue;

  // Check if the cell matches the legend filter
  const isLegendFiltered = legendClickedValue && matchesLegend;

  // When the partner filter is active, dim the non-partner regions (color
  // remapping handles dimming for the density filters instead).
  const displayColor =
    isPartnerFilter && !data?.isPartner ? "#F5F5F5" : color;

  useEffect(() => {
    if (isClicked?.value) {
      setIsSvgSelected(!isTileClicked);
      setIsDefaultState(false);
      setIsAnyCellClicked(true); // Set to true when any cell is clicked
    } else if (filteredKey && legendClickedValue) {
      const isFilterSelectedClicked =
        (isRegionSelected && filteredValues?.has(data?.region)) ||
        (isCohortSelected && filteredValues?.has(data.cohort?.toUpperCase()));

      const isLegendClicked =
        legendClickedValue && data?.performance === legendClickedValue;
      const svgSelect = isFilterSelectedClicked && isLegendClicked;
      setIsLegendSelected(svgSelect);
      setIsSvgSelected(!svgSelect);
    } else if (filteredKey) {
      const isFilterSelectedClicked =
        (isRegionSelected && filteredValues?.has(data?.region)) ||
        (isCohortSelected && filteredValues?.has(data.cohort?.toUpperCase()));
      setIsLegendSelected(isFilterSelectedClicked);
      setIsSvgSelected(!isFilterSelectedClicked);
    } else if (legendClickedValue) {
      const isLegendClicked = legendClickedValue && matchesLegend;
      setIsLegendSelected(isLegendClicked);
      setIsSvgSelected(!isLegendClicked);
    } else {
      setIsDefaultState(true);
      setIsSvgSelected(false);
      setIsLegendSelected(false);
      setIsAnyCellClicked(false); // Reset when no cell is clicked
    }
  }, [isClicked, legendClickedValue, filteredKey, filteredData]);

  useEffect(() => {
    if (selectedStateAbbrev !== null) {
      // The URL is the source of truth: if this cell's state is the selected
      // one, keep it highlighted. This must be idempotent (set, not toggle) so
      // unrelated param changes (e.g. quickFilter/globalFilter) don't flip the
      // selection off. Deselection happens by removing `state` from the URL.
      if (selectedStateAbbrev === data?.abbrev) {
        setIsClicked((prev) => {
          if (
            prev?.value &&
            prev?.row === data?.row &&
            prev?.column === data?.column
          ) {
            return prev; // Already selected on this cell - no change.
          }
          return {
            ...prev,
            value: true,
            row: data?.row,
            column: data?.column,
            data,
          };
        });
      }
    } else {
      setIsClicked({ value: false, row: null, column: null, data: null });
    }
  }, [searchParams]);

  return (
    <HexagonContainer
      isSecondColumn={isSecondColumn}
      isFirstColumn={isFirstColumn}
      isSecondRows={isSecondRows}
      spaceMultipler={index}
      size={size}
      row={row}
      column={column}
    >
      {loading ? (
        <StyledSvg
          width={size * 2}
          height={size * 2}
          viewBox={`0 0 ${size * 2} ${size * 2}`}
          fill="none"
        >
          <defs>
            <linearGradient
              id="shimmerGradient"
              x1="0%"
              y1="50%"
              x2="100%"
              y2="50%"
            >
              <stop offset="0%" stopColor="#EDEDED">
                <animate
                  attributeName="offset"
                  values="-1; 1"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="50%" stopColor="#FEFEFE">
                <animate
                  attributeName="offset"
                  values="0; 2"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="100%" stopColor="#EDEDED">
                <animate
                  attributeName="offset"
                  values="1; 3"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>
          </defs>
          <path
            d={getHexagonPath(size, 5)} // Adjust the corner radius as needed
            fill="url(#shimmerGradient)" // Apply the animated gradient
            stroke="#EDEDED"
            strokeWidth="0.5"
            style={{
              shapeRendering: "geometricPrecision",
              transform: "rotate(30deg)",
              transformOrigin: "50% 50%",
            }}
          />
        </StyledSvg>
      ) : (
        <StyledSvg
          className={`${marketName}-hexagon`}
          onMouseEnter={(e) => {
            setIsHovered(true);
            handleMouseEnter(e, { ...data, color });
          }}
          onMouseMove={(e) => handleMouseMove(e, { ...data, color })}
          onMouseLeave={() => {
            setIsHovered(false);
            handleMouseLeave();
          }}
          onClick={(e) => {
            setIsHovered(false);
            handleMouseClick(e, { row, column, ...data, color });
          }}
          width={size * 2}
          height={size * 2}
          viewBox={`0 0 ${size * 2} ${size * 2}`}
          fill="none"
        >
          <path
            d={getHexagonPath(size, 5)}
            fill={
              isAnyCellClicked && !isTileClicked && !isHovered
                ? "#F5F5F5"
                : displayColor
            }
            stroke={
              isTileClicked || isLegendFiltered
                ? border
                : isAnyCellClicked && !isTileClicked && !isHovered
                  ? "#F5F5F5"
                  : displayColor
            }
            strokeWidth={isTileClicked || isLegendFiltered ? 2 : 0.5} // Increase stroke width when clicked or legend-filtered
            style={{
              shapeRendering: "geometricPrecision",
              transform: "rotate(30deg)",
              transformOrigin: "50% 50%",
              transition: "fill 0.8s ease-in-out, stroke 0.8s ease-in-out",
            }}
          />
          {showPartnerBadge &&
            (() => {
              // "P" partner-location badge centred near the top of the hexagon.
              const iconSize = size * 0.4;
              return (
                <image
                  href={partnerIcon}
                  x={size - iconSize / 2}
                  y={size * 0.4 - iconSize / 2}
                  width={iconSize}
                  height={iconSize}
                  style={{ pointerEvents: "none" }}
                />
              );
            })()}
          <text
            x="50%"
            y={showPartnerBadge ? "45%" : "40%"}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={
              isAnyCellClicked && !isTileClicked && !isHovered
                ? "#000000"
                : getFontColor(displayColor)
            }
            fontSize={labelFontSize}
            fontFamily="Inter"
            fontWeight={700}
            style={{
              textRendering: "optimizeLegibility",
              transition: "fill 0.8s ease-in-out",
            }}
          >
            {marketName}
          </text>
          <text
            x="50%"
            y={showPartnerBadge ? "70%" : "65%"}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={
              isAnyCellClicked && !isTileClicked && !isHovered
                ? "#000000"
                : getFontColor(displayColor)
            }
            fontSize={labelFontSize * 0.8}
            fontFamily="Inter"
            fontWeight={500}
            style={{
              textRendering: "optimizeLegibility",
              transition: "fill 0.8s ease-in-out",
            }}
          >
            {data?.value}
          </text>
        </StyledSvg>
      )}
    </HexagonContainer>
  );
};

export default HexagonCells;
